/**
 * Date utility functions for consistent timezone handling
 *
 * IMPORTANT: Backend stores timestamps as DateTime (without timezone offset),
 * but they represent UTC time. When serialized to JSON, they may not include
 * the "Z" suffix, causing JavaScript to interpret them as local time.
 *
 * These utilities ensure all timestamps are correctly interpreted as UTC.
 */

/**
 * Parse a date string as UTC, even if it doesn't have the "Z" suffix
 *
 * @param dateString - ISO date string (e.g., "2025-10-30T19:38:26" or "2025-10-30T19:38:26Z")
 * @returns Date object with correct UTC interpretation
 */
export const parseAsUTC = (dateString: string): Date => {
  // If the date string doesn't end with 'Z', add it to force UTC interpretation
  const utcString = dateString.endsWith('Z') ? dateString : `${dateString}Z`;
  return new Date(utcString);
};

/**
 * Format a date string as a localized date/time string
 * Always interprets the input as UTC before converting to local time
 *
 * @param dateString - ISO date string from backend
 * @returns Formatted date string in user's local timezone (e.g., "10/30/2025, 12:38:26 PM")
 */
export const formatDateTime = (dateString: string): string => {
  const date = parseAsUTC(dateString);
  return date.toLocaleString();
};

/**
 * Format a date string with custom locale options
 * Always interprets the input as UTC before converting to local time
 *
 * @param dateString - ISO date string from backend
 * @param options - Intl.DateTimeFormatOptions for custom formatting
 * @param locale - Locale string (default: 'en-US')
 * @returns Formatted date string in user's local timezone
 */
export const formatDateTimeWithOptions = (
  dateString: string,
  options: Intl.DateTimeFormatOptions,
  locale: string = 'en-US'
): string => {
  const date = parseAsUTC(dateString);
  return date.toLocaleDateString(locale, options);
};

/**
 * Format a date as a relative time string (e.g., "2 hours ago", "Just now")
 * Always interprets the input as UTC before calculating time difference
 *
 * @param dateString - ISO date string from backend
 * @returns Relative time string
 */
export const formatRelativeTime = (dateString: string): string => {
  const date = parseAsUTC(dateString);
  const now = new Date();
  const diffInSeconds = Math.floor((now.getTime() - date.getTime()) / 1000);

  if (diffInSeconds < 60) {
    return 'Just now';
  } else if (diffInSeconds < 3600) {
    const minutes = Math.floor(diffInSeconds / 60);
    return `${minutes} minute${minutes !== 1 ? 's' : ''} ago`;
  } else if (diffInSeconds < 86400) {
    const hours = Math.floor(diffInSeconds / 3600);
    return `${hours} hour${hours !== 1 ? 's' : ''} ago`;
  } else if (diffInSeconds < 604800) {
    const days = Math.floor(diffInSeconds / 86400);
    return `${days} day${days !== 1 ? 's' : ''} ago`;
  } else {
    return date.toLocaleDateString();
  }
};
